import React, { useState, useMemo } from 'react';
import { getNumerologyReading } from '../services/geminiService';
import { getPreloadedNumerologyReading } from '../data/numerologyPreloaded';
import { SparklesIcon, ChevronRightIcon, StarIcon, PhoneIcon, UserIcon, CalendarIcon, RefreshCwIcon, CheckIcon } from './Icons';
import { useClient } from '../contexts/ClientContext';

// Table de correspondance pythagoricienne (A-Z -> 1-9)
const LETTER_VALUES: Record<string, number> = {
    A: 1, J: 1, S: 1,
    B: 2, K: 2, T: 2,
    C: 3, L: 3, U: 3,
    D: 4, M: 4, V: 4,
    E: 5, N: 5, W: 5,
    F: 6, O: 6, X: 6,
    G: 7, P: 7, Y: 7,
    H: 8, Q: 8, Z: 8,
    I: 9, R: 9
};

const NUMBER_TITLES: Record<number, { title: string; subtitle: string; element: string; color: string; desc: string }> = {
    1: { title: "Le Pionnier & Le Bâtisseur", subtitle: "Indépendance, Leadership, Force d'Initiative", element: "Feu", color: "from-amber-500 to-rose-600", desc: "Premier élan créateur, esprit d'avant-garde et autonomie souveraine." },
    2: { title: "Le Diplomate & L'Harmonisateur", subtitle: "Sensibilité, Partenariat, Intuition Émotionnelle", element: "Eau", color: "from-cyan-500 to-blue-600", desc: "Sens aigu de l'écoute, médiation subtile et force d'union avec autrui." },
    3: { title: "Le Créateur & L'Expressif", subtitle: "Communication, Joie de Vivre, Rayonnement", element: "Air", color: "from-yellow-400 to-amber-500", desc: "Verbe captivant, enthousiasme artistique et talent pour inspirer le monde." },
    4: { title: "L'Architecte & Le Structurant", subtitle: "Stabilité, Rigueur, Endurance Réalisatrice", element: "Terre", color: "from-emerald-500 to-teal-600", desc: "Bâtisseur inébranlable des fondations solides et de la sécurité durable." },
    5: { title: "L'Aventurier & Le Chercheur de Liberté", subtitle: "Changement, Curiosité, Magnétisme Évolutif", element: "Air", color: "from-indigo-400 to-purple-500", desc: "Goût du voyage, soif de renouveau et adaptabilité exceptionnelle." },
    6: { title: "Le Protecteur & Le Cœur Sacré", subtitle: "Bienveillance, Harmonie Familiale, Dévouement", element: "Terre", color: "from-pink-500 to-rose-500", desc: "Pilier d'amour inconditionnel, sens du devoir et gardien de l'équilibre." },
    7: { title: "Le Penseur Mystique & L'Éveillé", subtitle: "Sagesse, Spiritualité, Analyse Profonde", element: "Eau", color: "from-purple-500 to-indigo-600", desc: "Recherche de la vérité cachée, intuition philosophique et vision intérieure." },
    8: { title: "Le Conquérant & Le Visionnaire", subtitle: "Ambition, Pouvoir Juste, Réussite Concrète", element: "Feu", color: "from-amber-600 to-yellow-500", desc: "Maîtrise matérielle, sens aigu des affaires et puissance de manifestation." },
    9: { title: "L'Altruiste & Le Guide Universel", subtitle: "Générosité, Idéalisme, Conscience Cosmique", element: "Cosmos", color: "from-violet-500 to-pink-600", desc: "Dépassement du soi, compassion universelle et clôture des grands cycles." },
    11: { title: "Le Grand Maître Spirituel & Visionnaire", subtitle: "Intuition Supérieure, Éveil, Révélation", element: "Lumière", color: "from-cyan-400 via-teal-400 to-indigo-500", desc: "Canal intuitif direct, illumination spirituelle et guide des consciences." },
    22: { title: "Le Grand Bâtisseur Universel", subtitle: "Génie Réalisateur, Projets d'Envergure Mondiale", element: "Éther", color: "from-amber-400 via-rose-500 to-purple-600", desc: "Capacité suprême de matérialiser les rêves collectifs les plus audacieux." },
    33: { title: "Le Guide Céleste & Maître de l'Amour", subtitle: "Compassion Suprême, Dévouement à l'Humanité", element: "Amour Divin", color: "from-rose-400 via-pink-400 to-amber-300", desc: "Vibration christique de service désintéressé et d'élévation planétaire." }
};

export const Numerology: React.FC = () => {
    const { client, recordDivinationActivity } = useClient();
    
    const [fullName, setFullName] = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingStep, setLoadingStep] = useState(0);

    const [calculatedLifePath, setCalculatedLifePath] = useState<number | null>(null);
    const [calculatedExpression, setCalculatedExpression] = useState<number | null>(null);
    const [calculatedPersonalYear, setCalculatedPersonalYear] = useState<number | null>(null);
    const [result, setResult] = useState<any | null>(null);
    const [activeTab, setActiveTab] = useState<'forces' | 'defis' | 'amour' | 'travail'>('forces');
    const [selectedGalleryNumber, setSelectedGalleryNumber] = useState<number | null>(null);

    const loadingMessages = [
        "Réduction théosophique de votre date de naissance...",
        "Calcul de la fréquence vibratoire de votre prénom...",
        "Harmonisation de la clé karmique selon Pythagore...",
        "Révélation de votre Année Personnelle et cycles d'évolution..."
    ];

    // Calcul du Chemin de Vie
    const computeLifePath = (dateStr: string): number => {
        const parts = dateStr.split('-');
        if (parts.length !== 3) return 7;
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];

        const reduceNum = (n: number | string): number => {
            let sum = String(n).split('').reduce((acc, d) => acc + parseInt(d, 10), 0);
            while (sum > 9 && sum !== 11 && sum !== 22 && sum !== 33) {
                sum = String(sum).split('').reduce((acc, d) => acc + parseInt(d, 10), 0);
            }
            return sum;
        };

        const rDay = reduceNum(day);
        const rMonth = reduceNum(month);
        const rYear = reduceNum(year);

        return reduceNum(rDay + rMonth + rYear);
    };

    // Calcul du Nombre d'Expression (Prénom/Nom)
    const computeExpression = (nameStr: string): number => {
        if (!nameStr.trim()) return 3;
        const clean = nameStr.toUpperCase().replace(/[^A-Z]/g, '');
        if (!clean) return 3;

        let sum = 0;
        for (let i = 0; i < clean.length; i++) {
            sum += LETTER_VALUES[clean[i]] || 0;
        }

        while (sum > 9 && sum !== 11 && sum !== 22) {
            sum = String(sum).split('').reduce((acc, d) => acc + parseInt(d, 10), 0);
        }
        return sum || 3;
    };

    // Calcul de l'Année Personnelle
    const computePersonalYear = (dateStr: string): number => {
        const parts = dateStr.split('-');
        if (parts.length !== 3) return 1;
        const month = parseInt(parts[1], 10);
        const day = parseInt(parts[2], 10);
        const currentYear = new Date().getFullYear();

        let sum = day + month + currentYear;
        while (sum > 9) {
            sum = String(sum).split('').reduce((acc, d) => acc + parseInt(d, 10), 0);
        }
        return sum || 1;
    };

    // Calculs en direct au fur et à mesure de la saisie
    const livePreviewLifePath = useMemo(() => {
        if (!birthDate) return null;
        try {
            return computeLifePath(birthDate);
        } catch {
            return null;
        }
    }, [birthDate]);

    const livePreviewExpression = useMemo(() => {
        if (!fullName.trim()) return null;
        try {
            return computeExpression(fullName);
        } catch {
            return null;
        }
    }, [fullName]);

    const handleCalculate = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!birthDate) return;

        setLoading(true);
        setResult(null);
        setLoadingStep(0);

        const interval = setInterval(() => {
            setLoadingStep(prev => (prev < loadingMessages.length - 1 ? prev + 1 : prev));
        }, 650);

        const lifePath = computeLifePath(birthDate);
        const expression = computeExpression(fullName);
        const personalYear = computePersonalYear(birthDate);

        setCalculatedLifePath(lifePath);
        setCalculatedExpression(expression);
        setCalculatedPersonalYear(personalYear);

        try {
            const minWait = new Promise(res => setTimeout(res, 2400));
            const apiPromise = getNumerologyReading(lifePath);
            const [_, apiRes] = await Promise.all([minWait, apiPromise]);

            let finalRes: any;
            if (apiRes && apiRes.description) {
                finalRes = { number: lifePath, ...apiRes };
            } else {
                finalRes = getPreloadedNumerologyReading(lifePath);
            }

            setResult(finalRes);

            if (client) {
                recordDivinationActivity(
                    'numerology',
                    'Thème Numérologique',
                    `Chemin de Vie N°${lifePath} • Expression N°${expression}`,
                    { birthDate, fullName, lifePath, expression, personalYear, result: finalRes }
                );
            }
        } catch (err) {
            const fallback = getPreloadedNumerologyReading(lifePath);
            setResult(fallback);
        } finally {
            clearInterval(interval);
            setLoading(false);
        }
    };

    const handleReset = () => {
        setResult(null);
        setCalculatedLifePath(null);
        setCalculatedExpression(null);
        setCalculatedPersonalYear(null);
        setFullName('');
        setBirthDate('');
        setSelectedGalleryNumber(null);
    };

    const info = calculatedLifePath ? NUMBER_TITLES[calculatedLifePath] || NUMBER_TITLES[7] : null;

    return (
        <div className="min-h-screen bg-[#08081A] text-white pt-6 pb-28 px-4 max-w-4xl mx-auto animate-fade-in relative">
            
            {/* Lueur d'ambiance cosmique */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-80 bg-gradient-to-b from-cyan-600/15 via-purple-600/10 to-transparent blur-[100px] pointer-events-none"></div>

            {/* EN-TÊTE PRINCIPAL */}
            <div className="text-center mb-8 relative z-10">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-cyan-950/80 via-indigo-950/80 to-purple-950/80 border border-cyan-500/40 shadow-[0_0_20px_rgba(6,182,212,0.25)] mb-3">
                    <SparklesIcon className="w-3.5 h-3.5 text-cyan-300 animate-pulse" />
                    <span className="text-[11px] font-bold uppercase tracking-widest text-cyan-200">
                        Science Sacrée des Nombres & Destin
                    </span>
                </div>
                
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-transparent bg-clip-text bg-gradient-to-r from-white via-cyan-100 to-purple-200 tracking-tight">
                    Thème Numérologique
                </h1>
                
                <p className="text-xs sm:text-sm text-purple-200/80 max-w-xl mx-auto mt-2.5 leading-relaxed font-light">
                    Découvrez votre Chemin de Vie, votre Nombre d'Expression et l'énergie directrice de votre Année Personnelle selon la tradition pythagoricienne.
                </p>
            </div>

            {/* GALERIE D'EXPLORATION RAPIDE DES 12 VIBRATIONS SACRÉES */}
            {!result && !loading && (
                <div className="mb-8 p-5 sm:p-6 rounded-3xl bg-gradient-to-b from-[#140E36]/90 via-[#0D0824]/90 to-[#08081A] border border-purple-800/40 shadow-xl backdrop-blur-xl">
                    <div className="flex items-center justify-between mb-3.5 border-b border-purple-900/40 pb-2.5">
                        <span className="text-xs font-bold uppercase tracking-wider text-cyan-300 flex items-center gap-1.5">
                            <StarIcon className="w-3.5 h-3.5 text-amber-400" />
                            <span>Les 12 Vibrations Fondamentales (Cliquez pour explorer)</span>
                        </span>
                        <span className="text-[10px] text-purple-300/60 hidden sm:inline">Nombres Simples & Maîtres</span>
                    </div>

                    <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 gap-2">
                        {Object.entries(NUMBER_TITLES).map(([numStr, item]) => {
                            const n = parseInt(numStr, 10);
                            const isSelected = selectedGalleryNumber === n;
                            return (
                                <button
                                    key={n}
                                    type="button"
                                    onClick={() => setSelectedGalleryNumber(isSelected ? null : n)}
                                    className={`py-2 px-1 rounded-2xl flex flex-col items-center justify-center border transition-all cursor-pointer ${
                                        isSelected 
                                            ? 'bg-gradient-to-b from-cyan-500 to-indigo-600 text-white border-cyan-300 shadow-[0_0_15px_rgba(6,182,212,0.6)] scale-105'
                                            : 'bg-[#18103A]/70 border-purple-900/40 hover:border-cyan-400/60 text-purple-200 hover:text-white hover:-translate-y-0.5'
                                    }`}
                                >
                                    <span className="text-base sm:text-lg font-serif font-black">{n}</span>
                                    <span className="text-[8px] uppercase tracking-tighter opacity-80 truncate max-w-full">
                                        {n >= 11 ? 'Maître' : item.element}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Aperçu détaillé de la vibration cliquée dans la galerie */}
                    {selectedGalleryNumber && (
                        <div className="mt-4 p-4 rounded-2xl bg-[#1C1242]/90 border border-cyan-500/40 animate-fade-in flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-inner">
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="px-2 py-0.5 rounded-md bg-cyan-400 text-black text-[10px] font-black font-mono">
                                        N° {selectedGalleryNumber}
                                    </span>
                                    <h4 className="text-xs sm:text-sm font-serif font-bold text-white">
                                        {NUMBER_TITLES[selectedGalleryNumber].title}
                                    </h4>
                                    <span className="text-[10px] text-amber-300 font-bold bg-amber-950/60 px-2 py-0.5 rounded-full border border-amber-500/30">
                                        {NUMBER_TITLES[selectedGalleryNumber].element}
                                    </span>
                                </div>
                                <p className="text-xs text-purple-200/90 mt-1 font-light leading-relaxed">
                                    {NUMBER_TITLES[selectedGalleryNumber].desc}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setSelectedGalleryNumber(null)}
                                className="text-[10px] text-gray-400 hover:text-white underline shrink-0 cursor-pointer"
                            >
                                Fermer l'aperçu
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* FORMULAIRE DE CALCUL INTERACTIF */}
            {!result && !loading && (
                <div className="bg-gradient-to-b from-[#130E2E]/95 via-[#0E0924]/95 to-[#08081A]/95 border-2 border-purple-800/50 hover:border-cyan-500/60 focus-within:border-cyan-400 rounded-3xl p-6 sm:p-8 shadow-[0_0_40px_rgba(6,182,212,0.15)] transition-all space-y-6 backdrop-blur-xl relative">
                    
                    <div className="flex items-center justify-between border-b border-purple-900/50 pb-3">
                        <div className="flex items-center gap-2">
                            <SparklesIcon className="w-4 h-4 text-cyan-400" />
                            <h3 className="text-xs font-bold uppercase tracking-wider text-white">
                                Vos Coordonnées Vibratoires
                            </h3>
                        </div>
                        <span className="text-[10px] text-purple-300/70 font-mono">
                            Calcul Théosophique Immédiat
                        </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        
                        {/* Champ Prénom & Nom */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold uppercase tracking-wider text-purple-200 flex items-center justify-between">
                                <span className="flex items-center gap-1.5">
                                    <UserIcon className="w-3.5 h-3.5 text-purple-400" />
                                    <span>Prénom & Nom (Optionnel)</span>
                                </span>
                                {livePreviewExpression && (
                                    <span className="text-[10px] font-mono text-cyan-300 bg-cyan-950/80 px-2 py-0.5 rounded-full border border-cyan-500/40">
                                        Expression : N°{livePreviewExpression}
                                    </span>
                                )}
                            </label>
                            <input
                                type="text"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                placeholder="Ex : Marie Dupont"
                                className="w-full bg-[#1A1138]/70 border border-purple-900/60 focus:border-cyan-400 rounded-2xl py-3 px-4 text-xs sm:text-sm text-white placeholder-purple-400/40 focus:outline-none transition-all shadow-inner"
                            />
                            <p className="text-[10px] text-purple-300/60">
                                Révèle vos dons innés et l'expression extérieure de votre personnalité.
                            </p>
                        </div>

                        {/* Champ Date de Naissance */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold uppercase tracking-wider text-cyan-300 flex items-center justify-between">
                                <span className="flex items-center gap-1.5">
                                    <CalendarIcon className="w-3.5 h-3.5 text-cyan-400" />
                                    <span>Date de Naissance (Requise)</span>
                                </span>
                                {livePreviewLifePath && (
                                    <span className="text-[10px] font-mono text-amber-300 bg-amber-950/80 px-2 py-0.5 rounded-full border border-amber-500/40 animate-pulse">
                                        Chemin de Vie : N°{livePreviewLifePath}
                                    </span>
                                )}
                            </label>
                            <input
                                type="date"
                                value={birthDate}
                                onChange={(e) => setBirthDate(e.target.value)}
                                className="w-full bg-[#1A1138]/70 border border-purple-900/60 focus:border-cyan-400 rounded-2xl py-3 px-4 text-xs sm:text-sm text-white focus:outline-none transition-all shadow-inner cursor-pointer"
                                required
                            />
                            <p className="text-[10px] text-purple-300/60">
                                Base fondamentale du calcul du Chemin de Vie pythagoricien.
                            </p>
                        </div>

                    </div>

                    {/* Bouton de Soumission Principal */}
                    <button
                        onClick={() => handleCalculate()}
                        disabled={!birthDate}
                        className={`w-full py-4 rounded-full font-black text-xs uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-2.5 ${
                            birthDate
                                ? 'bg-gradient-to-r from-cyan-500 via-indigo-600 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white shadow-[0_0_30px_rgba(6,182,212,0.5)] hover:shadow-[0_0_40px_rgba(168,85,247,0.7)] hover:scale-[1.02] active:scale-95 cursor-pointer border border-cyan-300/40'
                                : 'bg-gray-800/80 text-gray-500 border border-gray-700/60 cursor-not-allowed opacity-50'
                        }`}
                    >
                        <SparklesIcon className="w-4 h-4 text-cyan-200" />
                        <span>Calculer Mon Thème Numérologique Complet</span>
                        <ChevronRightIcon className="w-4 h-4 text-white" />
                    </button>

                </div>
            )}

            {/* ANIMATION DE CALCUL PYTHAGORICIEN SUPÉRIEUR */}
            {loading && (
                <div className="max-w-lg mx-auto bg-gradient-to-b from-[#180E38] to-[#0D0721] border-2 border-cyan-500/50 rounded-3xl p-8 sm:p-12 shadow-[0_0_50px_rgba(6,182,212,0.3)] text-center space-y-6 animate-fade-in relative overflow-hidden">
                    
                    {/* Roues doubles pythagoriciennes animées */}
                    <div className="relative w-28 h-28 mx-auto flex items-center justify-center">
                        <div className="absolute inset-0 rounded-full border-4 border-dashed border-cyan-500/40 animate-spin" style={{ animationDuration: '6s' }}></div>
                        <div className="absolute inset-2 rounded-full border-2 border-purple-400/40 border-b-purple-300 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '2s' }}></div>
                        <div className="absolute inset-4 rounded-full bg-gradient-to-tr from-cyan-600/30 to-purple-600/30 blur-md"></div>
                        <span className="text-2xl font-serif font-black text-cyan-300 animate-pulse relative z-10">
                            {livePreviewLifePath || '7'}
                        </span>
                    </div>

                    <div>
                        <span className="text-[10px] font-black uppercase tracking-widest text-cyan-400 block mb-1">
                            Calcul Théosophique & Karmique
                        </span>
                        <h3 className="text-xl font-serif font-bold text-white">
                            Décodage des Nombres Sacrés
                        </h3>
                        <p className="text-xs text-purple-200/90 font-mono mt-3 min-h-[2.5rem] flex items-center justify-center px-4">
                            {loadingMessages[loadingStep]}
                        </p>
                    </div>

                    {/* Barre de Progression Énergétique */}
                    <div className="w-full bg-black/60 rounded-full h-2.5 overflow-hidden border border-cyan-500/40 p-0.5">
                        <div 
                            className="bg-gradient-to-r from-cyan-400 via-indigo-500 to-purple-400 h-full rounded-full transition-all duration-700 shadow-[0_0_15px_rgba(6,182,212,0.8)]"
                            style={{ width: `${((loadingStep + 1) / loadingMessages.length) * 100}%` }}
                        ></div>
                    </div>

                    <span className="text-[10px] text-purple-400/70 block">
                        Interrogation de la géométrie sacrée...
                    </span>
                </div>
            )}

            {/* RÉSULTAT COMPLET CALCULÉ */}
            {result && !loading && (
                <div className="space-y-6 animate-fade-in-up max-w-4xl mx-auto">
                    
                    {/* BANDEAU SUPÉRIEUR : LES 3 NOMBRES MAJEURS */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        
                        {/* 1. Chemin de Vie (Majeur) */}
                        <div className="bg-gradient-to-br from-[#1C123D] via-[#140D2E] to-[#0D081F] border-2 border-cyan-400/80 rounded-3xl p-6 text-center shadow-[0_0_35px_rgba(6,182,212,0.35)] relative overflow-hidden group">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-400/10 rounded-full blur-2xl pointer-events-none"></div>
                            
                            <span className="text-[10px] font-black uppercase tracking-wider text-cyan-400 block mb-1">
                                Chemin de Vie (Destin)
                            </span>
                            <span className="text-5xl sm:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-white to-purple-300 font-serif block my-1 group-hover:scale-105 transition-transform">
                                {calculatedLifePath}
                            </span>
                            <span className="text-xs font-bold text-amber-300 bg-amber-950/70 px-2.5 py-0.5 rounded-full border border-amber-500/40">
                                Élément {info?.element}
                            </span>
                        </div>

                        {/* 2. Nombre d'Expression */}
                        <div className="bg-[#12102A]/95 border border-purple-800/60 rounded-3xl p-6 text-center shadow-xl relative overflow-hidden">
                            <span className="text-[10px] font-black uppercase tracking-wider text-purple-300 block mb-1">
                                Nombre d'Expression
                            </span>
                            <span className="text-4xl sm:text-5xl font-black text-white font-serif block my-1">
                                {calculatedExpression}
                            </span>
                            <span className="text-xs text-purple-300/80">
                                {fullName ? `Vibration de "${fullName}"` : 'Talents & Personnalité'}
                            </span>
                        </div>

                        {/* 3. Année Personnelle */}
                        <div className="bg-[#12102A]/95 border border-amber-500/40 rounded-3xl p-6 text-center shadow-xl relative overflow-hidden">
                            <span className="text-[10px] font-black uppercase tracking-wider text-amber-300 block mb-1">
                                Année Personnelle {new Date().getFullYear()}
                            </span>
                            <span className="text-4xl sm:text-5xl font-black text-amber-300 font-serif block my-1">
                                {calculatedPersonalYear}
                            </span>
                            <span className="text-xs text-amber-200/80">
                                Cycle & Climat de l'année
                            </span>
                        </div>

                    </div>

                    {/* TITRE & DESCRIPTION PRINCIPALE DU CHEMIN DE VIE */}
                    <div className="bg-[#12102A]/95 border border-purple-900/60 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-4">
                        <div className="border-b border-purple-900/50 pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div>
                                <h2 className="text-xl sm:text-2xl font-serif font-bold text-white">
                                    {info?.title}
                                </h2>
                                <p className="text-xs sm:text-sm text-cyan-300 font-medium mt-1">
                                    {info?.subtitle}
                                </p>
                            </div>
                            <span className="self-start sm:self-center px-3 py-1 rounded-full bg-cyan-950 text-cyan-300 border border-cyan-500/40 text-[10px] font-bold uppercase tracking-wider">
                                Vibration N°{calculatedLifePath}
                            </span>
                        </div>

                        <p className="text-xs sm:text-sm text-purple-100/90 leading-relaxed font-light">
                            {result.description}
                        </p>
                    </div>

                    {/* ONGLETS DÉTAILLÉS : FORCES, DÉFIS, AMOUR, TRAVAIL */}
                    <div className="bg-[#12102A]/90 border border-purple-900/50 rounded-3xl p-6 shadow-2xl space-y-4">
                        
                        <div className="flex items-center gap-2 overflow-x-auto pb-2 border-b border-purple-900/40 no-scrollbar">
                            {[
                                { id: 'forces', label: 'Forces & Potentiels' },
                                { id: 'defis', label: 'Défis Karmiques' },
                                { id: 'amour', label: 'Amour & Cœur' },
                                { id: 'travail', label: 'Travail & Carrière' }
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id as any)}
                                    className={`px-4 py-2.5 rounded-2xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                                        activeTab === tab.id
                                            ? 'bg-gradient-to-r from-cyan-600 to-indigo-600 text-white shadow-lg scale-102 border border-cyan-400/50'
                                            : 'bg-black/40 text-purple-300/70 hover:text-white border border-transparent'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Contenu de l'onglet actif */}
                        <div className="pt-2 min-h-[120px]">
                            {activeTab === 'forces' && (
                                <div className="space-y-2.5 animate-fade-in">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-400 flex items-center gap-1.5">
                                        <CheckIcon className="w-3.5 h-3.5" />
                                        <span>Vos Atouts Majeurs & Forces :</span>
                                    </h4>
                                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-purple-200/90">
                                        {(result.qualities || []).map((q: string, i: number) => (
                                            <li key={i} className="flex items-start gap-2 bg-[#1A1238]/60 p-3 rounded-xl border border-purple-900/40">
                                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 shrink-0"></span>
                                                <span>{q}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {activeTab === 'defis' && (
                                <div className="space-y-2.5 animate-fade-in">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-amber-400">
                                        Points de Vigilance & Défis d'Évolution :
                                    </h4>
                                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-purple-200/90">
                                        {(result.challenges || []).map((c: string, i: number) => (
                                            <li key={i} className="flex items-start gap-2 bg-[#1A1238]/60 p-3 rounded-xl border border-purple-900/40">
                                                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 shrink-0"></span>
                                                <span>{c}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {activeTab === 'amour' && (
                                <div className="space-y-2 animate-fade-in">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-pink-400">
                                        Comportement Affectif & Relations Amoureuses :
                                    </h4>
                                    <p className="text-xs sm:text-sm text-purple-200/90 leading-relaxed bg-[#1A1238]/60 p-4 rounded-2xl border border-purple-900/40">
                                        {result.love}
                                    </p>
                                </div>
                            )}

                            {activeTab === 'travail' && (
                                <div className="space-y-2 animate-fade-in">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-cyan-400">
                                        Orientation Professionnelle & Réussite :
                                    </h4>
                                    <p className="text-xs sm:text-sm text-purple-200/90 leading-relaxed bg-[#1A1238]/60 p-4 rounded-2xl border border-purple-900/40">
                                        {result.work}
                                    </p>
                                </div>
                            )}
                        </div>

                    </div>

                    {/* CONSEIL DES NOMBRES */}
                    <div className="bg-gradient-to-r from-purple-950/90 via-[#160D30] to-indigo-950/90 p-6 sm:p-8 rounded-3xl border-2 border-amber-400/50 text-center space-y-2.5 shadow-[0_0_30px_rgba(245,158,11,0.2)]">
                        <span className="text-[10px] font-black uppercase tracking-widest text-amber-300 block">
                            Le Conseil Numérologique de l'Oracle
                        </span>
                        <p className="text-sm sm:text-base text-white italic font-serif leading-relaxed max-w-2xl mx-auto">
                            "{result.advice}"
                        </p>
                    </div>

                    {/* BOUTONS D'ACTION */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                        <button
                            onClick={handleReset}
                            className="w-full sm:w-auto px-7 py-4 rounded-full bg-[#150F33] hover:bg-[#1C1542] text-purple-200 text-xs font-bold border border-purple-800/60 transition-all cursor-pointer flex items-center justify-center gap-2"
                        >
                            <RefreshCwIcon className="w-3.5 h-3.5" />
                            <span>Calculer un autre thème</span>
                        </button>
                        
                        <a
                            href="tel:0985405274"
                            className="w-full sm:w-auto px-8 py-4 rounded-full bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-600 hover:from-emerald-500 hover:to-teal-500 text-white font-black text-xs uppercase tracking-wider shadow-[0_0_25px_rgba(16,185,129,0.4)] flex items-center justify-center gap-2.5 transition-all hover:scale-105"
                        >
                            <PhoneIcon className="w-4 h-4 text-emerald-100" />
                            <span>Étude Numérologique au 09 85 40 52 74</span>
                        </a>
                    </div>

                </div>
            )}

        </div>
    );
};

export default Numerology;
